import React, { useReducer } from 'react';
import { CSSTransition } from 'react-transition-group';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistory from '../../components/GridHistory';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearchMinimalContentLoader from '../../components/ListSearchMinimalContentLoader';
import PanelContainer from '../../components/PanelContainer';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import {updateHistory} from '../../helpers/grid-history';
import withProcessActions from '../../hocs/withProcessActions';
import withViewStatisticsAction from '../../hocs/withViewStatisticsAction';
import useApi from '../../hooks/useApi';
import {useGrid} from '../../hooks/useGrid';
import PageLayout from '../../layouts/PageLayout';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import PushProcessDialog from '../../scenes/Dialog/components/PushProcessDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import List from '../../scenes/List';
import SubscriptionProcessItem from '../../scenes/List/components/SubscriptionProcessItem';
import RecruiterSubscription from '../../scenes/RecruiterSubscription';
import SubscriptionSearch from '../../scenes/RecruiterSubscription/components/SubscriptionSearch';
import Sidebar from '../../scenes/Sidebar';
import InBoundsHistory from '../../scenes/Sidebar/components/InBoundsHistory';
import { ACTIONS, reducer } from './reducer';

import './styles.css';

const SubscriptionSearches = props => {
  const {
    history,
    processAction: { jobPush, jobViewApplicants },
    viewStatisticsJobExtended,
  } = props;

  const [state, dispatch] = useReducer(reducer, { selected: [] });

  const api = useApi();

  const grid = useGrid(ENDPOINT.COLLECTION.SUBSCRIPTION_SEARCHES, params => {
    updateHistory(history, params);
  });

  const handlePush = ({ ProcessID } = process) => {
    jobPush(ProcessID);
  };
  const handleViewStatistics = ({ ProcessID } = process) => {
    viewStatisticsJobExtended(ProcessID);
  };

  const handleViewApplicants = ({ ProcessID } = process) => {
    jobViewApplicants(ProcessID);
  };

  const handleMassPush = () => {
    if (!state.selected.length) {
      BasicDialog({
        bodyRender: 'Please select at least 2 processes',
      });
    } else if (state.selected.length < 2) {
      BasicDialog({
        bodyRender: 'Please select 2-5 processes',
      });
    } else {
      PushProcessDialog({
        type: 'Processes',
        determine: 'these',
      })
        .then(sendEmail => {
          api.post(
            ENDPOINT.ACTION.SUBSCRIPTION_MASS_PUSH_JOB,
            {
              sendEmail: sendEmail ? 1 : 0,
              ids: state.selected,
            },
            {
              handleError: response => ResponseErrorDialog({ response }),
              handleSuccess: handleDeselectAll,
            }
          );
        })
        .catch(() => {});
    }
  };

  const handleSelectChange = (id, value) => {
    if (value && state.selected.length > 4) {
      BasicDialog({
        bodyRender: 'You cannot push more than 5 processes at once',
      });
    } else {
      dispatch({
        type: value ? ACTIONS.SELECT : ACTIONS.DESELECT,
        data: id,
      });
    }
  };

  const handleDeselectAll = () => {
    dispatch({ type: ACTIONS.DESELECT_ALL });
  };

  return (
    <GridHistory history={history} grid={grid}>
      <PageLayout
        pageClass="subscription-searches"
        pageTitle="Subscription Searches"
        mainContent={
          <>
            {grid.data.init ? (
              <PanelContainer styles={{ marginBottom: '10px' }}>
                <div className="page__description">
                  <h3 className="page__subtitle">Subscription Searches</h3>
                </div>
                <div className="subscription-searches__search">
                  <SubscriptionSearch
                    search={grid.data.params.search}
                    searchPlaceholder="Search Process"
                    onSendSearchQueryHandler={grid.actions.search}
                    onDeselectAll={handleDeselectAll}
                    onPush={handleMassPush}
                    counter={state.selected.length}
                    hasPushButton={grid.data && grid.data.isAllowedMassPushJob}
                  />
                </div>
              </PanelContainer>
            ) : (
              <ListSearchMinimalContentLoader />
            )}

            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>

            {grid.data.init && (
              <CSSTransition
                in={grid.data.init}
                appear={true}
                timeout={ANIMATION_TIMEOUT_APPEAR}
                classNames="fade"
              >
                {grid.data.isFetching ? (
                  <div className="horizontal-loader__container">
                    <HorizontalLoader />
                  </div>
                ) : (
                  <RecruiterSubscription>
                    <List
                      items={grid.data.rows}
                      itemRender={item => (
                        <SubscriptionProcessItem
                          key={item.ProcessID}
                          data={item}
                          isSelectable={
                            grid.data && grid.data.isAllowedMassPushJob
                          }
                          isSelected={
                            state.selected.indexOf(item.ProcessID) !== -1
                          }
                          actions={{
                            handlePush,
                            handleViewStatistics,
                            handleViewApplicants,
                            handleSelectChange,
                          }}
                        />
                      )}
                      actions={{
                        onChangePage: grid.actions.readPage,
                      }}
                      pagination={{
                        page: grid.data.page,
                        count: grid.data.rowsCount,
                        perPage: grid.data.perPage,
                      }}
                    />
                  </RecruiterSubscription>
                )}
              </CSSTransition>
            )}
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              <InBoundsHistory isRecruiter />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistory>
  );
};

export default withViewStatisticsAction(
  withProcessActions(SubscriptionSearches)
);
